import React, { useState } from 'react';
import { User, Lock, Eye, EyeOff } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TextField } from '@atoms';
import { useAuth } from '@contexts/AuthContext';
import { PasswordChangeModal } from '@organisms/auth/PasswordChangeModal';
import toast from 'react-hot-toast';

// No longer need MUI theme - using our atomic components

export const AuthPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, passwordChangeRequired } = useAuth();

  // Handle authentication and password change flow
  React.useEffect(() => {
    if (isAuthenticated) {
      if (passwordChangeRequired) {
        // Show password change modal instead of navigating
        setShowPasswordChangeModal(true);
      } else {
        // Normal navigation
        const from = (location.state as any)?.from?.pathname || '/';
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, passwordChangeRequired, navigate, location]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoginLoading(true);
    
    try {
      const result = await login(email, password);
      if (result.success) {
        toast.success('Login successful!');
        if (result.passwordChangeRequired) {
          setShowPasswordChangeModal(true);
        } else {
          navigate('/', { replace: true });
        }
      } else {
        toast.error('Login failed. Please check your credentials.');
      }
    } catch (error) {
      toast.error('An error occurred during login.');
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handlePasswordChangeSuccess = async (_newToken: string) => {
    // Token is already updated in AuthContext and success toast is shown there
    setShowPasswordChangeModal(false);
    
    // Navigate to the intended page
    const from = (location.state as any)?.from?.pathname || '/';
    navigate(from, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background-secondary relative overflow-hidden">
      {/* Background Shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-accent-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary-300 rounded-full opacity-10 blur-3xl"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden">
          <div className="flex">
            {/* Left Section - Branding */}
            <div className="w-1/2 bg-gradient-to-br from-primary-500 to-primary-600 p-8 flex flex-col justify-center">
              <div className="text-white">
                {/* Logo and Brand */}
                <div className="mb-8">
                  <div className="mb-6 inline-block">
                    <img 
                      src="/andersens_logo.png" 
                      alt="Andersens Logo" 
                      className="h-20 w-auto"
                    />
                  </div>
                  <p className="text-primary-100 text-lg">
                    Invoice Processing Platform
                  </p>
                </div>

                {/* Feature Highlights */}
                <div className="space-y-6">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                    <span className="text-primary-100">AI-Powered Invoice Processing</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                    <span className="text-primary-100">Secure Document Management</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                    <span className="text-primary-100">Real-time Data Extraction</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section - Login Form */}
            <div className="w-1/2 p-8 flex flex-col justify-center">
              {/* User Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-secondary-600" />
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                {/* Email Field */}
                <TextField
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  variant="outlined"
                  fullWidth
                  required
                  disabled={isLoginLoading}
                  icon={User}
                  iconPosition="left"
                  size="medium"
                />

                {/* Password Field */}
                <div className="relative">
                  <TextField
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    variant="outlined"
                    fullWidth
                    required
                    disabled={isLoginLoading}
                    icon={Lock}
                    iconPosition="left"
                    size="medium"
                  />
                  {/* Password toggle button */}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoginLoading}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  disabled={isLoginLoading}
                  className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold py-3 px-4 rounded-lg hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isLoginLoading ? 'Signing in...' : 'LOGIN'}
                </button>


                {/* Forgot Password */}
                <div className="text-center">
                  <button
                    type="button"
                    className="text-secondary-600 hover:text-secondary-800 text-sm transition-colors"
                    disabled={isLoginLoading}
                  >
                    Forgot password?
                  </button>
                </div>

              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={showPasswordChangeModal}
        onSuccess={handlePasswordChangeSuccess}
        isRequired={true}
        title="Password Change Required"
        description="For security reasons, you must change your password before accessing the application."
      />
    </div>
  );
};
